<template>
    <div>
        <b-sidebar
            v-model="filtering.isSidebarVisible"
            :title="`${getAttributionName(filtering.selectedFilter)}`"
            width="400px"
            left
            bg-variant="white"
            backdrop
            shadow
        >
            <div class="sticky-top mb-25" :class="{'bg-white': skin == 'light'}">
                <div class="d-flex justify-content-between px-1 mb-50">
                    <b-link
                        class="mt-25 mr-75 pt-50 px-0"
                        variant="outline-secondary"
                        @click="selectAllFilterValues()"
                    >
                        <i class="fas fa-lg"
                            :class="{
                                'fa-check-square': filtering.valuesOfCurrentFilter.length == filtering.uniqValues.length + filtering.uniqValuesNotInData.length,
                                'fa-square': filtering.valuesOfCurrentFilter.length != filtering.uniqValues.length + filtering.uniqValuesNotInData.length
                            }"
                        ></i>
                    </b-link>
                    <b-input-group class="flex-grow-1 mr-25">
                        <b-input
                            :placeholder="$t('v4.filter')"
                            v-model="filtering.filterStringTemp"
                            @keydown.enter="applyFilterForFiltering"
                        />
                        <b-input-group-append>
                            <b-button
                                class="px-1"
                                size="sm"
                                variant="secondary"
                                @click="applyFilterForFiltering"
                            >
                                <i class="fas fa-search"></i>
                            </b-button>
                        </b-input-group-append>
                    </b-input-group>
                    <b-button
                        size="sm"
                        variant="primary"
                        @click="applyFilterValues()"
                        style="min-width:113px;"
                    >
                        <span class="pt-25">{{ $t('org.dashboard.Apply') }}</span>
                        <b-badge
                            variant="light"
                            class="text-dark ml-50"

                        >{{ filtering.valuesOfCurrentFilter.length > 100 ? '100+' : filtering.valuesOfCurrentFilter.length }}</b-badge>
                    </b-button>
                </div>
            </div>
            <div class="px-0 pb-2">
                <b-alert
                    v-if="filtering.uniqValues.length < 1"
                    show
                    class="p-2 mb-0 mx-1"
                    variant="danger"
                >
                    {{ $t('v4.There are no available values of X', { X: getAttributionName(filtering.selectedFilter) }) }}
                </b-alert>
                <p
                    v-for="val in filtering.sortedUniqValues"
                    :key="val"
                    class="d-flex m-0 font-weight-bold px-1 py-25"
                    :class="{'bg-warning': val.toString().toLowerCase().includes(filtering.filterString) && filtering.filterString != ''}"
                >
                    <b-checkbox
                        :value="val"
                        v-model="filtering.valuesOfCurrentFilter"
                    >
                        <span
                            :class="val=='N/A' ? 'text-danger' : ''"
                        >{{ val }}</span>
                    </b-checkbox>
                </p>
                <hr
                    v-if="filtering.uniqValuesNotInData.length"
                />
                <p
                    v-for="val in filtering.uniqValuesNotInData"
                    :key="val"
                    class="d-flex m-0 font-italic px-1 py-25"
                    :class="{'bg-warning': val.toString().toLowerCase().includes(filtering.filterString) && filtering.filterString != ''}"
                >
                    <b-checkbox
                        :value="val"
                        v-model="filtering.valuesOfCurrentFilter"
                    >
                        <span
                            :class="val=='N/A' ? 'text-danger' : ''"
                        >{{ val }}</span>
                    </b-checkbox>
                </p>
            </div>
        </b-sidebar>
        <div class="mb-0">
            <b-overlay
                :show="loading"
                rounded="sm"
            >
                <template #overlay v-if="loading">
                    <div class="text-center">
                        <p class="mb-50" v-if="currentDType === 'file'">
                            {{ $t('org.billing.Creating a CSV file') }}
                        </p>
                        <p class="mb-50" v-else-if="currentDType === 'email'">
                            {{ $t('org.billing.You have requested to send an email Please check back later') }}
                        </p>
                        <b-spinner />
                    </div>
                </template>
                <b-table-simple
                    fixed
                    outlined
                    responsive
                    class="mb-0"
                >
                    <colgroup>
                        <col style="width:200px;" />
                        <col />
                    </colgroup>
                    <b-thead>
                        <b-tr>
                            <b-td
                                class="p-50 align-top"
                            >
                                <b-select
                                    v-model="rendering.selected"
                                    :options="rendering.rendererNameOptions"
                                    :disabled="downloadMode"
                                    size="sm"
                                ></b-select>
                            </b-td>
                            <b-td
                                class="pt-50 pl-50 pr-0 pb-25 border-left"
                            >
                                <draggable :list="defaultAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))" group="sources" draggable=".item" @change="checkRemove">
                                    <b-button
                                        v-for="attr in defaultAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))"
                                        :key="attr"
                                        class="item mt-0 mr-25 mb-25 ml-0 pt-50"
                                        :class="{'pl-75': ['ymd', 'ym', 'd'].includes(attr)}"
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        <i v-if="['ymd', 'ym', 'w', 'd'].includes(attr)" class="fas fa-calendar mr-25"></i>
                                        {{ getAttributionName(attr).replace('TAG:', '') }}
                                    </b-button>
                                </draggable>
                                <slot name="dropdown-description"></slot>
                                <draggable v-if="descriptionItemsAttributes.length" :list="descriptionItemsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))" group="sources" draggable=".item" @change="checkRemove">
                                    <b-button
                                        v-for="attr in descriptionItemsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))"
                                        :key="attr"
                                        class="item mt-0 mr-25 mb-25 ml-0 pt-50"
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        {{ getAttributionName(attr).replace('TAG:', '') }}
                                    </b-button>
                                </draggable>
                                <slot name="dropdown-resource"></slot>
                                <draggable v-if="resourceTagsAttributes.length" :list="resourceTagsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))" group="sources" draggable=".item" @change="checkRemove">
                                    <b-button
                                        v-for="attr in resourceTagsAttributes.filter(attr => !rows.includes(attr) && !cols.includes(attr))"
                                        :key="attr"
                                        class="item mt-0 mr-25 mb-25 ml-0 pt-50"
                                        :class="{'pl-75': attr.includes('TAG:')}"
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        <i v-if="attr.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                        {{ getAttributionName(attr).replace('TAG:', '') }}
                                    </b-button>
                                </draggable>
                            </b-td>
                        </b-tr>
                        <b-tr>
                            <b-td
                                class="p-50 align-bottom"
                            >
                                <b-select
                                    v-model="outputFormatting.selected"
                                    :options="outputFormatting.pivotValueOptions"
                                    size="sm"
                                ></b-select>
                            </b-td>
                            <b-td
                                class="pt-50 pl-50 pr-0 pb-25 border-left"
                            >
                                <draggable :list="config.cols" group="sources" draggable=".item" @change="checkRemove">
                                    <b-button-group
                                        class="item mt-0 mb-25 ml-0 mr-25"
                                        v-for="item in config.cols"
                                        :key="item"
                                    >
                                        <b-button
                                            v-if="!downloadMode"
                                            class="pt-50"
                                            :class="{'pr-50': getAttributionFilterValueLength(item) > -1, 'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                            size="sm"
                                            :variant="filtering.itemNamesOfHavingFilterValues.includes(item) ? 'primary' : 'outline-primary'"
                                            @click="openFilterModal(item)"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                        </b-button>
                                        <span
                                            v-else
                                            class="btn btn-sm btn-outline-primary pt-50"
                                            :class="{'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                        </span>
                                        <b-button
                                            v-if="!downloadMode && getAttributionFilterValueLength(item) > 0"
                                            class="pt-50 px-50"
                                            variant="outline-primary"
                                            size="sm"
                                        >
                                            {{ getAttributionFilterValueLength(item) }}
                                        </b-button>
                                        <b-button
                                            class="pt-50 px-50"
                                            v-if="getAttributionFilterValueLength(item) > -0"
                                            variant="outline-primary"
                                            size="sm"
                                        >
                                            {{ getAttributionFilterValueLength(item) }}
                                        </b-button>
                                    </b-button-group>
                                </draggable>
                            </b-td>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr>
                            <b-td
                                class="pt-50 pl-50 pr-25 pb-25 align-top d-flex align-content-stretch"
                            >
                                <draggable
                                    :list="config.rows"
                                    group="sources"
                                    draggable=".item"
                                    @change="checkRemove"
                                    class="d-flex align-items-start flex-column flex-fill"
                                    style="padding-bottom:60%;"
                                >
                                    <b-button-group
                                        class="item mt-0 mb-25 ml-0 mr-25"
                                        v-for="item in config.rows"
                                        :key="item"
                                    >
                                        <b-button
                                            v-if="!downloadMode"
                                            class="pt-50"
                                            :class="{'pr-50': getAttributionFilterValueLength(item) > -1, 'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                            size="sm"
                                            :variant="filtering.itemNamesOfHavingFilterValues.includes(item) ? 'primary' : 'outline-primary'"
                                            @click="openFilterModal(item)"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                            <i
                                                v-if="getAttributionFilterValueLength(item) == 0"
                                                class="fas fa-caret-right fa-sm ml-25"
                                            />
                                        </b-button>
                                        <span
                                            v-else
                                            class="btn btn-sm btn-outline-primary pt-50"
                                            :class="{'pl-75': item.includes('TAG:') || ['ymd', 'ym', 'd'].includes(item)}"
                                        >
                                            <i v-if="item.includes('TAG:')" class="fas fa-tag mr-25"></i>
                                            <i v-else-if="['ymd', 'ym', 'd'].includes(item)" class="fas fa-calendar mr-25"></i>
                                            {{ getAttributionName(item).replace('TAG:', '') }}
                                        </span>
                                        <b-button
                                            v-if="!downloadMode && getAttributionFilterValueLength(item) > 0"
                                            class="pt-50 px-50"
                                            variant="outline-primary"
                                            size="sm"
                                        >
                                            {{ getAttributionFilterValueLength(item) }}
                                        </b-button>
                                    </b-button-group>
                                </draggable>
                            </b-td>
                            <b-td class="p-0 align-top">
                                <!-- 로딩이 아니고 서버 메시지 있는 경우 -->
                                <!-- 내부 사용자이거나 고객인데 응답코드가 406이 아닌 경우 (e.g 429) -->
                                <div v-if="serverMessage && ((internalUser) || (!internalUser && statusCode !== 205))">
                                    <b-alert
                                        show
                                        class="text-center p-5 m-50"
                                        variant="danger"
                                    >
                                        <feather-icon icon="AlertTriangleIcon" />
                                        {{ serverMessage }}
                                    </b-alert>
                                </div>
                                <!-- 대용량 파일 다운로드가 완료되서 S3 링크가 제공된 경우 -->
                                <div v-else-if="(currentDType === 'file' || downloadMode) && downloadStatus === 'DONE'">
                                    <b-alert
                                        v-if="downloadLink"
                                        variant="secondary"
                                        class="text-center p-5 m-50"
                                        show
                                    >
                                        <b-button-group class="mb-50">
                                            <b-button
                                                variant="primary"
                                                @click="handleClickDownload"
                                            >
                                                <feather-icon icon="DownloadIcon" />
                                                {{ $t('v4.Download CSV') }}
                                            </b-button>
                                            <b-link
                                                v-if="locale != 'en'"
                                                class="btn btn-outline-secondary"
                                                target="_new"
                                                :href="`/user-guide?path=docs%2F${namespace}%2F${locale}%2Fcsv-encoding%2Fdocument.json`"
                                            >
                                                <feather-icon icon="FileTextIcon" />
                                                {{ $t('v4.Encoding Conversion Guide') }}
                                            </b-link>
                                        </b-button-group>

                                        <p
                                            class="mb-25 text-center small"
                                        >
                                            ※ {{ $t('org.billing.CSV file contains Korean characters will not be displayed correctly in the Excel program') }}
                                        </p>
                                        <p
                                            v-if="locale != 'en'"
                                            class="mb-0 text-center small"
                                        >
                                            {{  $t('org.billing.Please refer to the encoding conversion guide') }}
                                        </p>
                                    </b-alert>
                                </div>
                                <!-- 이메일 전송 완료 -->
                                <div v-else-if="currentDType === 'email' && downloadStatus === 'DONE'">
                                    <b-alert
                                        variant="secondary"
                                        class="text-center p-5 m-50"
                                        show
                                    >
                                        <p class="m-0">
                                            {{ $t('org.billing.You have requested to send an email Please check back later') }}
                                        </p>
                                    </b-alert>
                                </div>
                                <!-- 다운로드 모드에서 statusCode가 있거나 일반모드에서 205인 경우 -->
                                <div v-else-if="!loading && ((downloadMode && statusCode) || (!downloadMode && statusCode === 205)) && downloadStatus !== 'DONE'"
                                     :style="downloadMode ? 'height: 100%; display: flex; align-items: center;' : ''">
                                    <b-alert
                                        variant="secondary"
                                        class="text-center p-5 m-50 w-100"
                                        :style="downloadMode ? 'min-height: 300px; display: flex; flex-direction: column; justify-content: center;' : ''"
                                        show
                                    >
                                        <!-- 다운로드 모드에서 데이터 없음(204) -->
                                        <template v-if="downloadMode && statusCode === 204">
                                            <p class="mb-0 text-center">{{ $t('org.message.There are no usage details') }}</p>
                                        </template>
                                        <!-- 다운로드 모드에서 데이터 있음 또는 일반 모드 -->
                                        <template v-else>
                                            <div v-if="downloadMode" class="mb-50 text-center">
                                                <p class="mb-25">{{ $t('v4["Download Mode is enabled."]') }}</p>
                                                <p class="mb-0">{{ $t('v4["Request CSV download"]') }}</p>
                                            </div>
                                            <p v-else class="mb-50 text-center">{{ $t('org.billing.Too many rows retrieved to display on the screen') }}</p>
                                            <div class="d-flex justify-content-center mb-50">
                                                <b-button-group>
                                                    <b-button variant="primary" @click="largeFileDownload('file')">
                                                        <feather-icon icon="FileIcon" />
                                                        {{ $t('v4.CSV Download Request') }}
                                                    </b-button>
                                                <b-button v-if="!downloadMode" variant="warning" @click="largeFileDownload('email')">
                                                    <feather-icon icon="MailIcon" />
                                                    {{ $t('v4.Send Email') }}
                                                </b-button>
                                                </b-button-group>
                                            </div>
                                            <p class="mb-50 text-center small" v-if="!downloadMode">
                                                ※ {{ $t('org.billing.download the file from your browser or receive a link via email') }}
                                            </p>
                                        </template>
                                    </b-alert>
                                </div>

                                <!-- 로딩이 아니고 서버 메시지 없고 사이즈가 많은 경우 -->
                                <div v-else-if="!viewTable && size > ROW_MAX_LIMIT">
                                    <b-alert
                                        variant="secondary"
                                        class="text-center p-5 m-50"
                                        show
                                    >
                                        <div class="alert-body">
                                            <p class="mb-50">{{ $tc('org.billing.Too much data to display (n) Please download CSV file', size) }}</p>
                                            <p class="mb-50">{{ $t('org.billing.Do you want to display on this page or download CSV file?')}}</p>
                                            <b-button-group>
                                                <b-button variant="primary" @click="csvDownload(pivotData)">
                                                    <feather-icon icon="DownloadIcon" />
                                                    {{ $t('v4.CSV Download') }}
                                                </b-button>
                                                <b-button variant="warning" @click="pivotViewTable">
                                                    <feather-icon icon="EyeIcon" />
                                                    {{ $t('v4.View table') }}
                                                </b-button>
                                            </b-button-group>
                                        </div>
                                    </b-alert>
                                </div>
                                <div
                                    v-else-if="!downloadMode && data.length < 1"
                                >
                                    <!-- 데이터가 없는 경우 -->
                                    <b-alert
                                        variant="warning"
                                        class="text-center p-5 m-50"
                                        show
                                    >

                                        <div class="alert-body">
                                            {{ $t('org.message.There are no usage details') }}
                                        </div>
                                    </b-alert>
                                </div>
                                <!-- 기본 TABLE UI 노출 -->
                                <VuePivottable
                                    v-else
                                    v-model="config"
                                    :data="data"
                                    :aggregators="aggregators"
                                    :aggregatorName="aggregatorName"
                                    :rendererName="rendering.selected"
                                    :rows="rows"
                                    :cols="cols"
                                    :vals="vals"
                                />
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
            </b-overlay>
        </div>
    </div>
</template>

<script>
import useCostPivot from '@/composables/useCostPivot'
import useAppConfig from '@core/app-config/useAppConfig'
import { VuePivottable, PivotUtilities, Renderer } from 'vue-pivottable'
import { getCurrentInstance, computed, ref, toRefs, watch, reactive } from '@vue/composition-api'
import draggable from 'vuedraggable'
import bus from '@/libs/bus'
export default {
    components: {
        VuePivottable,
        draggable
    },
    model: {
        prop: 'items',
        event: 'sync:items'
    },
    data () {
        return {
            ROW_MAX_LIMIT: 10000,
            MENU_MAX_LIMIT: 15000
        }
    },
    props: {
        items: {
            type: Object,
            default: function () {
                return {
                    rows: ['usageAccountId', 'unblendedrate'],
                    cols: ['ym'],
                    compositSources: ['usageAccountId', 'ym'],
                    value: 'unblendedCost'
                }
            }
        },
        data: {
            type: Array,
            default: function () {
                return []
            }
        },
        pivotFilter: {
            type: Object,
            default: function () {
                return {}
            }
        },
        downloadStatus: {
            type: String,
            default: ''
        },
        downloadLink: {
            type: String,
            default: ''
        },
        internalUser: {
            type: Boolean,
            default: false
        },
        serverMessage: {
            type: String,
            default: null
        },
        statusCode: {
            type: [Number, String],
            default: 200
        },
        loading: {
            type: Boolean,
            default: false
        },
        fieldStatus: {
            type: Object,
            default: function () {
                return {
                    description: false,
                    resources: false
                }
            }
        },
        size: {
            type: Number,
            default: 0
        },
        accountMap: {
            type: Object,
            default: function () {
                return {}
            }
        },
        downloadMode: {
            type: Boolean,
            default: false
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        // const costPivotValue = ref('unblendedCost')
        const { data, items, pivotFilter, fieldStatus, accountMap, downloadLink, downloadMode } = toRefs(props)
        const compositSources = computed(() => items.value.compositSources)
        const aggregatorName = 'Sum'
        // const rendererNames = ['Table', 'Table Heatmap', 'Export Table TSV']
        const { attributeDefaultItems, attributeDescriptionItems, attributeResourceItems, locales, pivotValues } = useCostPivot()
        const attributeItems = computed(() => attributeDefaultItems.value.concat(attributeDescriptionItems.value).concat(attributeResourceItems.value))
        const rendererName = ref('Table')
        const currentDType = ref(null)
        const changeProject = computed(() => vm.$store.getters.changeProject)
        const { skin } = useAppConfig()
        const config = ref({
            cols: ['ym'],
            rows: ['usageAccountId'],
            vals: ['value'],
            valueFilter: {}
        })

        const aggregators = computed(() => {
            const usFmt = PivotUtilities.numberFormat({ digitsAfterDecimal: 3 })
            return ((tpl) => ({
                Sum: tpl.sum(usFmt)
            })
            )(PivotUtilities.aggregatorTemplates)
        })
        const renderers = computed(() => {
            const TableRenderer = Renderer.TableRenderer
            return (() => ({
                'Table': TableRenderer.Table,
                'Table Heatmap': TableRenderer['Table Heatmap'],
                // 'Table Col Heatmap': TableRenderer['Table Col Heatmap'],
                // 'Table Row Heatmap': TableRenderer['Table Row Heatmap'],
                'Export Table TSV': TableRenderer['Export Table TSV']
            })
            )()
        })
        const savedFilter = ref({})
        const value = computed(() => items.value.vals)
        // const rows = ref(['usageAccountId'])
        // const cols = ref(['ym'])
        const cols = computed(() => config.value.cols)
        const rows = computed(() => config.value.rows)
        const vals = ref(['value'])
        const hiddenAttributes = ['value']
        const viewTable = ref(false)
        const disabledFromDragDrop = ref([])
        ///
        // temp
        const checkRemove = evt => {
            if (evt.removed) {
                if (!items.value.cols.includes(evt.removed.element) && !items.value.rows.includes(evt.removed.element)) {
                    delete filtering.holdingValues[evt.removed.element]
                    context.emit('update:pivotFilter', [...filtering.appliedFilters.filter(item => item.key !== evt.removed.element)].reduce((acc, item) => {
                        return { ...acc, [item.key]: item.values }
                    }, {}))
                }
            }
        }

        const rendering = reactive({
            selected: 'Table',
            rendererNames: ['Table', 'Table Heatmap', 'Export Table TSV'],
            rendererNameOptions: computed(() => rendering.rendererNames.map(item => ({
                value: item,
                text: vm.$t(`v4.${item}`)
            })))
        })

        const outputFormatting = reactive({
            selected: 'unblendedCost',
            pivotValueOptions: computed(() => pivotValues.value.map(item => ({
                value: item.value,
                text: item.name
            })))
        })

        const getAttributionName = key => {
            return attributeMap.value[key] ? attributeMap.value[key].name : key
        }

        const getAttributionFilterValueLength = key => {
            if (filtering.appliedFilters.find(item => item.key === key) && filtering.appliedFilters.find(item => item.key === key).values.length) {
                return filtering.appliedFilters.find(item => item.key === key).values.length
            } else if (!attributeMap.value[key] || !attributeMap.value[key].removeFilter) {
                return 0
            } else {
                return -1
            }
        }

        const filtering = reactive({
            isSidebarVisible: false,
            selectedFilter: null,
            uniqValues: computed(() => filtering.selectedFilter
                ? data.value
                    .map(item => item[filtering.selectedFilter])
                    .map(val => val || (['resourceId', 'reservedInstancesId', 'savingsPlan_savingsPlanArn'].includes(filtering.selectedFilter) || filtering.selectedFilter.includes('TAG:') ? 'N/A' : null))
                    .filter(val => val)
                    .filter((obj, pos, arr) => (arr.indexOf(obj) === pos))
                : []
            ),
            sortedUniqValues: computed(() => filtering.filterString === '' ? filtering.uniqValues.sort((a, b) => a > b ? 1 : -1)
                : filtering.uniqValues.sort(a => a.toLowerCase().includes(filtering.filterString) ? -1 : 1)
            ),
            filterString: '',
            filterStringTemp: '',
            valuesOfCurrentFilter: [],
            itemNamesOfHavingFilterValues: computed(() => [...items.value.cols, ...items.value.rows].filter(a => data.value.map(item => Object.keys(item)).some(b => b.includes(a)))),
            appliedFilters: computed(() => Object.keys(pivotFilter.value).map(key => ({ key, values: pivotFilter.value[key] }))),
            holdingValues: {},
            uniqValuesNotInData: computed(() => filtering.holdingValues[filtering.selectedFilter]?.filter(a => !filtering.uniqValues.includes(a)) ?? [])
        })

        const applyFilterForFiltering = () => {
            filtering.filterString = filtering.filterStringTemp.toLowerCase()
        }

        const openFilterModal = key => {
            if (getAttributionFilterValueLength(key) < 0) {
                bus.$emit('request:warning', { title: 'Info', content: vm.$t('org.billing.Filtering is not supported for selected item') })
            } else if (!filtering.itemNamesOfHavingFilterValues.includes(key)) {
                bus.$emit('request:warning', { title: 'Info', content: vm.$t('org.billing.Filtering values ​​could not be loaded because the data is too large or the combination of filters is not appropriate') })
            } else {
                filtering.filterString = ''
                filtering.filterStringTemp = ''
                filtering.selectedFilter = key
                filtering.valuesOfCurrentFilter = filtering.appliedFilters.find(item => item.key === key) ? filtering.appliedFilters.find(item => item.key === key).values : []

                setTimeout(() => {
                    filtering.isSidebarVisible = true
                }, 300)
            }
        }

        const selectAllFilterValues = () => {
            if (filtering.valuesOfCurrentFilter.length === filtering.uniqValues.length + filtering.uniqValuesNotInData.length) {
                filtering.valuesOfCurrentFilter = []
            } else {
                filtering.valuesOfCurrentFilter = [...filtering.uniqValues, ...filtering.uniqValuesNotInData]
            }
        }

        const keepAllValuesForFilter = () => {
            [...items.value.cols, ...items.value.rows].forEach(item => {
                if (!filtering.holdingValues[item]) {
                    filtering.holdingValues[item] = []
                }
            })
            Object.keys(filtering.holdingValues).forEach(key => {
                if (![...items.value.cols, ...items.value.rows].includes(key)) {
                    delete filtering.holdingValues[key]
                }
            })
            data.value.forEach(item => {
                Object.keys(item).forEach(key => {
                    if (key !== 'value' && filtering.holdingValues[key] && !filtering.holdingValues[key].includes(item[key] || 'N/A')) {
                        filtering.holdingValues[key].push(item[key] || 'N/A')
                    }
                })
            })
        }

        const applyFilterValues = () => {
            keepAllValuesForFilter()

            context.emit('update:pivotFilter', [...filtering.appliedFilters.filter(item => item.key !== filtering.selectedFilter), {
                key: filtering.selectedFilter,
                values: filtering.valuesOfCurrentFilter
            }].reduce((acc, item) => {
                return { ...acc, [item.key]: item.values }
            }, {}))

            context.emit('reload:pivotFilter')

            filtering.isSidebarVisible = false

            setTimeout(() => {
                filtering.selectedFilter = null
                filtering.valuesOfCurrentFilter = []
            }, 300)
        }

        const defaultAttributes = computed(() => {
            return attributeItems.value.filter(item => {
                return item.type === 'default'
            }).map(item => item.key)
        })
        const descriptionItemsAttributes = computed(() => {
            return attributeItems.value.filter(item => {
                return fieldStatus.value[item.type] && item.type === 'description'
            }).map(item => item.key)
        })
        const resourceTagsAttributes = computed(() => {
            return attributeItems.value.filter(item => {
                return fieldStatus.value[item.type] && item.type === 'resource'
            }).map(item => item.key)
        })
        const attributeOfDescriptionType = computed(() => attributeItems.value.filter(item => item.type === 'description').map(item => item.key))
        const attributeOfResourceType = computed(() => attributeItems.value.filter(item => item.type === 'resource').map(item => item.key))
        const setFilter = (filter) => {
            context.emit('sync:filter', filter)
        }

        const updateValueFilter = (valueFilter) => {
            if (Object.keys(valueFilter).length) {
                const removeFilterKeys = Object.keys(valueFilter)
                    .filter(key =>
                        items.value.cols &&
                        items.value.rows &&
                        !items.value.cols.includes(key) &&
                        !items.value.rows.includes(key) &&
                        key !== 'value'
                    )
                removeFilterKeys.forEach(key => delete valueFilter[key])
                const filter = {
                    cols: items.value.cols,
                    rows: items.value.rows,
                    valueFilter
                }
                setFilter(filter)
            }
        }
        const namespace = process.env.VUE_APP_NAMESPACE
        const locale = computed(() => vm.$t('org.locale'))
        const attributeMap = computed(() => {
            return attributeItems.value.reduce((accumulator, currentValue) => {
                accumulator[currentValue.key] = currentValue
                return accumulator
            }, {})
        })
        const pivotData = computed(() => {
            const propsOptions = {
                locales: locales,
                data: data.value,
                aggregators: aggregators.value,
                aggregatorName: aggregatorName,
                rendererName: rendererName.value,
                renderers: renderers.value,
                valueFilter: savedFilter.value,
                rows: items.value.rows,
                cols: items.value.cols,
                vals: vals.value,
                hiddenAttributes: hiddenAttributes.value,
                disabledFromDragDrop: disabledFromDragDrop.value,
                defaultAttributes: defaultAttributes.value,
                menuLimit: 15000
            }
            const PivotData = PivotUtilities.PivotData
            return new PivotData(propsOptions)
        })
        const csvDownload = () => {
            const items = pivotData.value.props.cols.concat(pivotData.value.props.rows)
            // const attrs = attributeItems.value.filter(item => items.includes(item.key)).map(item => item.key).concat([costPivotValue.value])
            const attrs = items.concat([outputFormatting.selected])
            if (attrs.indexOf('usageAccountId') > -1) {
                attrs.splice(attrs.indexOf('usageAccountId'), 0, 'projectName')
            }
            const list = pivotData.value.getFilteredData()
            let tData = [attrs]
            const results = list.map(item => {
                const row = []
                tData[0].forEach((t) => {
                    if (t === outputFormatting.selected) {
                        row.push(item.value)
                    } else if (t === 'projectName') {
                        const endIndex = item.usageAccountId.lastIndexOf(')')
                        const usageAccountId = item.usageAccountId.substring(endIndex - 12, endIndex)
                        row.push(accountMap.value[usageAccountId])
                    } else if (t === 'usageAccountId') {
                        const endIndex = item.usageAccountId.lastIndexOf(')')
                        const usageAccountId = item.usageAccountId.substring(endIndex - 12, endIndex)
                        row.push(usageAccountId)
                    } else {
                        row.push(item[t])
                    }
                })
                return row
            })
            tData = tData.concat(results)
            const tmp = tData.map(data => `"${data.join('","')}"`)
            const csv = [tmp.join('\n')]
            const csvFile = new Blob(['\ufeff', csv], { type: 'text/csv' })
            const url = window.URL.createObjectURL(csvFile)
            const downloadLink = document.createElement('a')
            downloadLink.download = 'billing.exported.csv'
            downloadLink.href = url
            downloadLink.click()
            window.URL.revokeObjectURL(url)
        }

        const pivotViewTable = () => {
            viewTable.value = !viewTable.value
        }
        const largeFileDownload = (dType) => {
            currentDType.value = dType
            context.emit('download:largeFile', { dType })
        }
        const handleClickDownload = () => {
            if (downloadLink.value) {
                window.location.href = downloadLink.value
            }
        }

        watch(changeProject, (val, oldVal) => {
            filtering.holdingValues.usageAccountId = []
            filtering.holdingValues.group = []
        })

        watch(() => props.downloadMode, (newVal, oldVal) => {
            if (!newVal && oldVal) {
                currentDType.value = null
            }
            // 모드 전환 시 필터링 상태 초기화
            filtering.holdingValues = {}
            filtering.valuesOfCurrentFilter = []
            filtering.selectedFilter = null
            filtering.filterString = ''
            filtering.filterStringTemp = ''
        })

        watch(() => props.items, () => {
            config.value.rows = props.items.rows
            config.value.cols = props.items.cols
        }, {
            deep: true
        })
        const sources = computed(() => ({ rows: config.value.rows, cols: config.value.cols }))
        watch(sources, (value, oldValue) => {
            const newCompositSources = [...value.rows, ...value.cols].sort()
            if (JSON.stringify(newCompositSources) !== JSON.stringify(compositSources.value)) {
                const newItems = Object.assign({}, {
                    ...items.value,
                    rows: value.rows,
                    cols: value.cols
                })
                context.emit('sync:items', newItems)
                context.emit('sync:compositSource', value)

                // 다운로드 모드에서 필드 변경 시 currentDType 초기화
                if (downloadMode.value && currentDType.value === 'file') {
                    currentDType.value = null
                }
            }
        }, {
            immediate: false,
            deep: true
        })
        watch(() => outputFormatting.selected, (value, oldValue) => {
            const newItems = Object.assign({}, {
                ...items.value,
                value
            })
            context.emit('sync:items', newItems)
        })

        return {
            skin,
            pivotValues,
            locales,
            aggregators,
            aggregatorName,
            rendererName,
            renderers,
            savedFilter,
            value,
            vals,
            hiddenAttributes,
            disabledFromDragDrop,
            defaultAttributes,
            descriptionItemsAttributes,
            resourceTagsAttributes,
            attributeOfDescriptionType,
            attributeOfResourceType,
            namespace,
            locale,
            attributeMap,
            viewTable,
            config,
            rows,
            cols,
            currentDType,
            updateValueFilter,
            handleClickDownload,
            csvDownload,
            pivotViewTable,
            largeFileDownload,
            checkRemove,
            rendering,
            outputFormatting,
            filtering,
            getAttributionName,
            getAttributionFilterValueLength,
            applyFilterForFiltering,
            openFilterModal,
            selectAllFilterValues,
            applyFilterValues
        }
    }
}
</script>

<style lang="scss">
@import "./pivottable.scss";
</style>
